/* eslint-disable @typescript-eslint/no-explicit-any -- reading a heterogeneous field
   record back out requires an open value type; the public props stay tied to the
   caller's own state. */
import { SlidersHorizontal } from 'lucide-react'
import { useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'

import type { FilterField, FilterFields, FilterSpec } from './define'
import { MultiSelectFilter } from './multi-select-filter'
import { NumericRangeFilter } from './numeric-range-filter'
import { SelectFilter } from './select-filter'
import { TriStateFilter } from './tri-state-filter'

/**
 * One entry in a group: either a single field, or two fields shown as one range
 * control. Ranges are the common case in this app and two separate number inputs
 * labelled "Min score" and "Max score" read as unrelated filters.
 */
export type AdvancedEntry<TState> = keyof TState | { range: [keyof TState, keyof TState]; label?: string }

export interface AdvancedGroup<TState> {
    title: string
    description?: string
    entries: AdvancedEntry<TState>[]
}

export interface AdvancedFiltersProps<TFields extends FilterFields> {
    spec: FilterSpec<TFields>
    search: ReturnType<FilterSpec<TFields>['parse']>
    onChange: (patch: Partial<ReturnType<FilterSpec<TFields>['parse']>>) => void
    groups: AdvancedGroup<ReturnType<FilterSpec<TFields>['parse']>>[]
    /** Fields already on the filter bar, so the trigger's count does not double-count. */
    quickKeys?: (keyof ReturnType<FilterSpec<TFields>['parse']>)[]
    triggerLabel?: string
}

/**
 * Every filter a list has, rendered from its schema.
 *
 * The duplicates queue has sixty of them. The legacy page hand-wrote each control and
 * its own binding, which is most of why `cardDuplicateFilters.ts` reached a thousand
 * lines; here a field's `kind` already says what control it needs, so a group is a
 * list of keys and adding a filter to the API means adding one line to the spec.
 */
export function AdvancedFilters<TFields extends FilterFields>({
    spec,
    search,
    onChange,
    groups,
    quickKeys = [],
    triggerLabel = 'All filters'
}: AdvancedFiltersProps<TFields>) {
    const [open, setOpen] = useState(false)

    const quick = new Set(quickKeys as string[])
    const advancedCount = Object.entries(spec.fields).filter(
        ([key, field]) => !quick.has(key) && !(field as FilterField<any>).isEmpty((search as any)[key])
    ).length

    const clearAdvanced = () => {
        const patch: Record<string, unknown> = { page: 1 }
        for (const [key, field] of Object.entries(spec.fields)) {
            if (!quick.has(key)) patch[key] = (field as FilterField<any>).default
        }
        onChange(patch as never)
    }

    return (
        <>
            {/* Sits in the filter bar's cluster, so it grows with the other triggers. */}
            <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="pointer-coarse:h-10">
                <SlidersHorizontal />
                {triggerLabel}
                {advancedCount > 0 && (
                    <Badge variant="neutral" className="tabular ml-0.5">
                        {advancedCount}
                    </Badge>
                )}
            </Button>

            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent side="right" className="w-full sm:max-w-md">
                    <SheetHeader>
                        <SheetTitle>{triggerLabel}</SheetTitle>
                        <SheetDescription>
                            Everything the endpoint can filter on. Changes apply as you make them and stay in the URL,
                            so a view built here can be shared.
                        </SheetDescription>
                    </SheetHeader>

                    <ScrollArea className="min-h-0 flex-1">
                        {/* Same inset as the sheet header and footer, so the groups line up
                            with the title. */}
                        <div className="flex flex-col gap-6 px-5 py-5">
                            {groups.map(group => (
                                <section key={group.title} className="flex flex-col gap-2.5">
                                    <div>
                                        <h3 className="text-sm font-semibold tracking-tight">{group.title}</h3>
                                        {group.description && (
                                            <p className="text-xs text-muted-foreground">{group.description}</p>
                                        )}
                                    </div>
                                    <div className="flex flex-wrap items-end gap-2">
                                        {group.entries.map(entry =>
                                            typeof entry === 'object' ? (
                                                <RangeControl
                                                    key={String(entry.range[0])}
                                                    spec={spec}
                                                    search={search}
                                                    onChange={onChange}
                                                    entry={entry}
                                                />
                                            ) : (
                                                <FieldControl
                                                    key={String(entry)}
                                                    spec={spec}
                                                    search={search}
                                                    onChange={onChange}
                                                    fieldKey={entry as string}
                                                />
                                            )
                                        )}
                                    </div>
                                </section>
                            ))}
                        </div>
                    </ScrollArea>

                    {/* `justify-between` holds these two at opposite ends of the sheet, so
                        they are isolated even at 360px and can take hit-slop safely. */}
                    <SheetFooter className="flex-row justify-between">
                        <Button
                            variant="ghost"
                            size="sm"
                            disabled={advancedCount === 0}
                            onClick={clearAdvanced}
                            className="touch-target">
                            Clear {advancedCount > 0 ? advancedCount : ''} filters
                        </Button>
                        <Button size="sm" onClick={() => setOpen(false)} className="touch-target">
                            Done
                        </Button>
                    </SheetFooter>
                </SheetContent>
            </Sheet>
        </>
    )
}

function RangeControl<TFields extends FilterFields>({
    spec,
    search,
    onChange,
    entry
}: {
    spec: FilterSpec<TFields>
    search: any
    onChange: (patch: any) => void
    entry: { range: [any, any]; label?: string }
}) {
    const [minKey, maxKey] = entry.range as [string, string]
    const field = spec.fields[minKey] as FilterField<any> | undefined
    if (!field) return null

    const isRatio = field.kind === 'ratio'
    const label = entry.label ?? field.label.replace(/^(min|max)\s*/i, '')

    return (
        <NumericRangeFilter
            label={isRatio ? `${label} %` : label}
            prefix={field.kind === 'cents' ? '$' : undefined}
            step={field.kind === 'number' ? 1 : isRatio ? 1 : 0.01}
            value={{ min: search[minKey], max: search[maxKey] }}
            onChange={next => onChange({ [minKey]: next.min, [maxKey]: next.max, page: 1 })}
        />
    )
}

function FieldControl<TFields extends FilterFields>({
    spec,
    search,
    onChange,
    fieldKey
}: {
    spec: FilterSpec<TFields>
    search: any
    onChange: (patch: any) => void
    fieldKey: string
}) {
    const field = spec.fields[fieldKey] as FilterField<any> | undefined
    if (!field) return null

    const value = search[fieldKey]
    const set = (next: unknown) => onChange({ [fieldKey]: next, page: 1 })

    switch (field.kind) {
        case 'text':
            return (
                <div className="flex min-w-40 flex-1 flex-col gap-1">
                    <Label htmlFor={`filter-${fieldKey}`}>{field.label}</Label>
                    <Input
                        id={`filter-${fieldKey}`}
                        value={value as string}
                        onChange={event => set(event.target.value)}
                        placeholder={field.label}
                    />
                </div>
            )

        case 'date':
            return (
                <div className="flex min-w-36 flex-col gap-1">
                    <Label htmlFor={`filter-${fieldKey}`}>{field.label}</Label>
                    <Input
                        id={`filter-${fieldKey}`}
                        type="date"
                        value={value as string}
                        onChange={event => set(event.target.value)}
                    />
                </div>
            )

        case 'enum':
            return (
                <SelectFilter
                    label={field.label}
                    options={field.options ?? []}
                    value={value as string}
                    onChange={next => set(next === '' ? field.default : next)}
                    anyLabel={field.default === '' ? 'Any' : undefined}
                    searchable={(field.options?.length ?? 0) > 8}
                />
            )

        case 'multiEnum':
            return (
                <MultiSelectFilter
                    label={field.label}
                    options={field.options ?? []}
                    value={value as string[]}
                    onChange={next => set(next)}
                />
            )

        case 'tri':
            return <TriStateFilter label={field.label} value={value as ''} onChange={next => set(next)} />

        case 'flag':
            return (
                <div className="flex min-w-40 items-center gap-2">
                    <Switch id={`filter-${fieldKey}`} checked={Boolean(value)} onCheckedChange={next => set(next)} />
                    <Label htmlFor={`filter-${fieldKey}`} className="font-normal">
                        {field.label}
                    </Label>
                </div>
            )

        /**
         * A free-form list has no option set to render, so it is edited as the
         * comma-separated text the operator would paste in anyway.
         */
        case 'list':
            return (
                <div className="flex min-w-40 flex-1 flex-col gap-1">
                    <Label htmlFor={`filter-${fieldKey}`}>{field.label}</Label>
                    <Input
                        id={`filter-${fieldKey}`}
                        value={(value as string[]).join(', ')}
                        onChange={event =>
                            set(
                                event.target.value
                                    .split(',')
                                    .map(part => part.trim())
                                    .filter(Boolean)
                            )
                        }
                        placeholder="Comma separated"
                    />
                </div>
            )

        default:
            return (
                <div className={cn('flex min-w-32 flex-col gap-1')}>
                    <Label htmlFor={`filter-${fieldKey}`}>{field.label}</Label>
                    <Input
                        id={`filter-${fieldKey}`}
                        type="number"
                        inputMode="decimal"
                        value={value === undefined ? '' : String(value)}
                        onChange={event => {
                            const parsed = Number(event.target.value)
                            set(event.target.value === '' || !Number.isFinite(parsed) ? undefined : parsed)
                        }}
                        className="tabular"
                    />
                </div>
            )
    }
}
